import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import FeatherIcon from "feather-icons-react";

import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Toolbar,
  Menu,
  Typography,
  Avatar,
  Button,
} from "@mui/material";

import PropTypes from "prop-types";
import headerDiwaliBackground from "../../../assets/images/header-diwali.png";

import NotificationDropdown from "./NotificationDropdown";
import ProfileDropdown from "./ProfileDropdown";

import { useAuth } from "../../../authentication/AuthProvider";
import { GetUserDetail } from "../../../services/user/index";

const isApprovalNotification = (item) => {
  const isApproval =
    item?.isApproval === true ||
    item?.IsApproval === true ||
    Number(item?.isApproval) === 1 ||
    Number(item?.IsApproval) === 1;

  const approvalRequired =
    item?.approvalRequired === true ||
    item?.ApprovalRequired === true ||
    Number(item?.approvalRequired) === 1 ||
    Number(item?.ApprovalRequired) === 1;

  const category = String(
    item?.category ?? item?.Category ?? ""
  )
    .trim()
    .toUpperCase();

  const actionCode = String(
    item?.actionCode ?? item?.ActionCode ?? ""
  )
    .trim()
    .toUpperCase();

  const actionType = Number(item?.actionType);

  const pending =
    item?.isApprovalPending === true ||
    item?.IsApprovalPending === true ||
    Number(item?.isApprovalPending) === 1 ||
    Number(item?.IsApprovalPending) === 1;

  return (
    isApproval ||
    approvalRequired ||
    category === "APPROVAL" ||
    actionCode === "APPROVAL_REQUIRED" ||
    (actionType === 6 && pending)
  );
};

const Header = ({
  sx,
  customClass,
  toggleSidebar,
  toggleMobileSidebar,
}) => {
  const auth = useAuth();

  const [userDetail, setUserDetail] =
    useState({});

  const [anchorEl, setAnchorEl] =
    useState(null);

  const [anchorEl4, setAnchorEl4] =
    useState(null);

  /*
   * =========================================================
   * USER DETAIL
   * =========================================================
   */
  const getUserDetail = useCallback(
    async () => {
      try {
        const response =
          await GetUserDetail();

        setUserDetail(
          response?.data || {}
        );
      } catch (error) {
        console.error(
          "User detail fetch failed:",
          error
        );
      }
    },
    []
  );

  useEffect(() => {
    getUserDetail();
  }, [getUserDetail]);

  /*
   * =========================================================
   * DYNAMIC NOTIFICATION COUNTS
   * =========================================================
   *
   * Notification behavior comes from emp.NotificationConfig via
   * emp.GetNotifications. Header does not know module numbers or
   * action numbers anymore.
   */
  const approvalNotificationCount =
    useMemo(() => {
      if (!Array.isArray(auth?.notifications)) {
        return 0;
      }

      return auth.notifications.filter((item) => {
        const bellEnabled =
          item?.bellEnabled !== false &&
          Number(item?.bellEnabled) !== 0 &&
          item?.BellEnabled !== false &&
          Number(item?.BellEnabled) !== 0;

        const notificationEnabled =
          item?.notificationEnabled !== false &&
          Number(item?.notificationEnabled) !== 0 &&
          item?.NotificationEnabled !== false &&
          Number(item?.NotificationEnabled) !== 0;

        const approvalPending =
          item?.isApprovalPending === true ||
          item?.IsApprovalPending === true ||
          Number(item?.isApprovalPending) === 1 ||
          Number(item?.IsApprovalPending) === 1;

        return (
          notificationEnabled &&
          bellEnabled &&
          isApprovalNotification(item) &&
          approvalPending
        );
      }).length;
    }, [auth?.notifications]);

  const unseenNormalNotificationCount =
    useMemo(() => {
      if (!Array.isArray(auth?.notifications)) {
        return 0;
      }

      return auth.notifications.filter((item) => {
        const bellEnabled =
          item?.bellEnabled !== false &&
          Number(item?.bellEnabled) !== 0 &&
          item?.BellEnabled !== false &&
          Number(item?.BellEnabled) !== 0;

        const notificationEnabled =
          item?.notificationEnabled !== false &&
          Number(item?.notificationEnabled) !== 0 &&
          item?.NotificationEnabled !== false &&
          Number(item?.NotificationEnabled) !== 0;

        const isSeen =
          item?.isSeen === true ||
          item?.IsSeen === true ||
          Number(item?.isSeen) === 1 ||
          Number(item?.IsSeen) === 1;

        return (
          notificationEnabled &&
          bellEnabled &&
          !isSeen
        );
      }).length;
    }, [auth?.notifications]);

  /*
   * =========================================================
   * BELL STATE
   * =========================================================
   *
   * Pending approval count gets priority.
   * Approval state is independent from Seen state.
   *
   * Example:
   * approval = 3, unseen normal = 10
   * => show 3
   *
   * approval = 0, unseen normal = 10
   * => show dot
   *
   * approval = 0, unseen normal = 0
   * => plain bell
   */
  const hasPendingApproval =
    approvalNotificationCount >
    0;

  const hasUnseenNormalNotification =
    !hasPendingApproval &&
    unseenNormalNotificationCount >
    0;

  /*
   * =========================================================
   * NOTIFICATION MENU
   * =========================================================
   */
  const handleClick = (event) => {
    // Opening the bell only opens the menu.
    // Notifications are marked seen by AuthProvider when their
    // popup is displayed. Pending approvals stay pending until
    // the actual approval/denial workflow is completed.
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /*
   * =========================================================
   * PROFILE MENU
   * =========================================================
   */
  const handleClick4 = (
    event
  ) => {
    setAnchorEl4(
      event.currentTarget
    );
  };

  const handleClose4 = () => {
    setAnchorEl4(null);
  };

  /*
   * =========================================================
   * AVATAR HELPERS
   * =========================================================
   */
  function stringToColor(
    string
  ) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (
      i = 0;
      i < string.length;
      i += 1
    ) {
      hash =
        string.charCodeAt(i) +
        ((hash << 5) -
          hash);
    }

    let color = "#";

    for (
      i = 0;
      i < 3;
      i += 1
    ) {
      const value =
        (hash >>
          (i * 8)) &
        0xff;

      color += `00${value.toString(
        16
      )}`.slice(-2);
    }

    /* eslint-enable no-bitwise */

    return color;
  }

  function stringAvatar(
    name
  ) {
    const safeName =
      String(
        name || ""
      ).trim();

    const parts =
      safeName
        .split(/\s+/)
        .filter(Boolean);

    const first =
      parts[0]?.[0] || "";

    const second =
      parts[1]?.[0] || "";

    return {
      sx: {
        bgcolor:
          stringToColor(
            safeName ||
            "User"
          ),
        width: "30px",
        height: "30px",
      },
      children:
        `${first}${second}` ||
        "U",
    };
  }

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */
  return (
    <AppBar
      sx={{
        ...sx,
        // Subtle Diwali strip: preserves the existing white/orange ERP header.
        backgroundColor: "#ffffff",
        backgroundImage: `url(${headerDiwaliBackground})`,
        backgroundSize: "100% auto", // Preserve the PNG aspect ratio; do not stretch or zoom-crop.
        backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat",
        borderBottom: "1px solid rgba(230, 150, 65, 0.18)",
        boxShadow: "0 3px 16px rgba(0, 0, 0, 0.035)",
      }}
      elevation={0}
      className={
        customClass
      }
    >
      <Toolbar>
        {/* Desktop menu */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={
            toggleSidebar
          }
          size="large"
          sx={{
            display: {
              lg: "flex",
              xs: "none",
            },
          }}
        >
          <FeatherIcon icon="menu" />
        </IconButton>

        {/* Mobile menu */}
        <IconButton
          size="large"
          color="inherit"
          aria-label="menu"
          onClick={
            toggleMobileSidebar
          }
          sx={{
            display: {
              lg: "none",
              xs: "flex",
            },
          }}
        >
          <FeatherIcon
            icon="menu"
            width="20"
            height="20"
          />
        </IconButton>

        {/* Company name */}
        <Box
          sx={{
            display: "flex",
            alignItems:
              "center",
            ml: 2,
            flexWrap:
              "nowrap",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight:
                "700",
              color:
                "primary.main",
              fontFamily:
                "'Inter', 'Segoe UI', Roboto, Helvetica, sans-serif",
              letterSpacing:
                "-0.5px",
              whiteSpace:
                "nowrap",
            }}
          >
            SSWhite Unified ERP
          </Typography>

          <Box
            sx={{
              height:
                "24px",
              width:
                "2px",
              backgroundColor:
                "divider",
              mx: 2,
              display: {
                xs: "none",
                md: "block",
              },
            }}
          />

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight:
                "500",
              color:
                "text.secondary",
              fontFamily:
                "'Inter', 'Segoe UI', Roboto, Helvetica, sans-serif",
              whiteSpace:
                "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems:
                "center",
              gap: 1,
            }}
          >
            One Platform

            <Box
              component="span"
              sx={{
                color:
                  "primary.main",
                opacity: 0.5,
              }}
            >
              •
            </Box>

            Every Department

            <Box
              component="span"
              sx={{
                color:
                  "primary.main",
                opacity: 0.5,
              }}
            >
              •
            </Box>

            Every Process
          </Typography>
        </Box>

        <Box flexGrow={1} />

        {/* ========================================== */}
        {/* NOTIFICATION BELL */}
        {/* ========================================== */}
        <IconButton
          size="large"
          aria-label={
            hasPendingApproval
              ? `${approvalNotificationCount} pending approvals`
              : hasUnseenNormalNotification
                ? "New notifications"
                : "Notifications"
          }
          color="inherit"
          aria-controls="notification-menu"
          aria-haspopup="true"
          onClick={
            handleClick
          }
        >
          {/*
           * CASE 1:
           * Approval required.
           *
           * Show numeric badge.
           */}
          {hasPendingApproval ? (
            <Badge
              badgeContent={
                approvalNotificationCount >
                  99
                  ? "99+"
                  : approvalNotificationCount
              }
              color="error"
              overlap="circular"
              max={99}
              sx={{
                position: "relative",
                "& .MuiBadge-badge": {
                  animation: "notificationBadgePulse 1.5s ease-in-out infinite",
                  right: -5,
                  top: 2,
                  transform: "none",
                  transformOrigin: "center",
                  zIndex: 2,
                },
                "@keyframes notificationBadgePulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(1.12)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  position: "relative",
                  zIndex: 1,
                  pr: 1.25,
                  transformOrigin: "50% 10%",
                  animation: "notificationBellSwing 1.8s ease-in-out infinite",
                  "@keyframes notificationBellSwing": {
                    "0%, 68%, 100%": {
                      transform: "rotate(0deg)",
                    },
                    "72%": {
                      transform: "rotate(-12deg)",
                    },
                    "77%": {
                      transform: "rotate(10deg)",
                    },
                    "82%": {
                      transform: "rotate(-7deg)",
                    },
                    "87%": {
                      transform: "rotate(4deg)",
                    },
                    "92%": {
                      transform: "rotate(-2deg)",
                    },
                  },
                }}
              >
                <FeatherIcon
                  icon="bell"
                  width="20"
                  height="20"
                  style={{ display: "block" }}
                />
              </Box>
            </Badge>
          ) : hasUnseenNormalNotification ? (
            /*
             * CASE 2:
             * No approval required but
             * unseen normal notifications exist.
             *
             * Show only a dot.
             */
            <Badge
              variant="dot"
              color="primary"
              overlap="circular"
            >
              <FeatherIcon
                icon="bell"
                width="20"
                height="20"
              />
            </Badge>
          ) : (
            /*
             * CASE 3:
             * Nothing pending/unseen.
             *
             * Plain bell.
             */
            <FeatherIcon
              icon="bell"
              width="20"
              height="20"
            />
          )}
        </IconButton>

        {/* Notification menu */}
        <Menu
          id="notification-menu"
          anchorEl={
            anchorEl
          }
          keepMounted
          open={Boolean(
            anchorEl
          )}
          onClose={
            handleClose
          }
          anchorOrigin={{
            horizontal:
              "right",
            vertical:
              "bottom",
          }}
          transformOrigin={{
            horizontal:
              "right",
            vertical:
              "top",
          }}
          sx={{
            "& .MuiMenu-paper":
            {
              width:
                "385px",
              right: 0,
              top:
                "70px !important",
            },

            "& .MuiList-padding":
            {
              p: "30px",
            },
          }}
        >
          <NotificationDropdown />
        </Menu>

        {/* Separator */}
        <Box
          sx={{
            width: "1px",
            backgroundColor:
              "rgba(0,0,0,0.1)",
            height: "25px",
            ml: 1,
            mr: 1,
          }}
        />

        {/* ========================================== */}
        {/* PROFILE */}
        {/* ========================================== */}
        <Button
          aria-label="menu"
          color="inherit"
          aria-controls="profile-menu"
          aria-haspopup="true"
          onClick={
            handleClick4
          }
        >
          <Box
            display="flex"
            alignItems="center"
          >
            {userDetail?.isPictureExists ? (
              <Avatar
                src={
                  userDetail.picture
                }
                alt={
                  userDetail.picture
                }
                sx={{
                  width:
                    "30px",
                  height:
                    "30px",
                }}
              />
            ) : (
              <Avatar
                {...stringAvatar(
                  `${userDetail?.firstName || ""} ${userDetail?.lastName || ""
                    }`.trim()
                )}
              />
            )}

            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "flex",
                },
                alignItems:
                  "center",
              }}
            >
              <FeatherIcon
                icon="chevron-down"
                width="20"
                height="20"
              />
            </Box>
          </Box>
        </Button>

        <Menu
          id="profile-menu"
          anchorEl={
            anchorEl4
          }
          keepMounted
          open={Boolean(
            anchorEl4
          )}
          onClose={
            handleClose4
          }
          sx={{
            "& .MuiMenu-paper":
            {
              width:
                "385px",
              right: 0,
              top:
                "70px !important",
            },

            "& .MuiList-padding":
            {
              p: "30px",
            },
          }}
        >
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
            >
              <Typography
                variant="h4"
                fontWeight="500"
              >
                User Profile
              </Typography>
            </Box>
          </Box>

          <ProfileDropdown
            userDetail={
              userDetail
            }
          />

          <Button
            sx={{
              mt: 2,
              display:
                "block",
              width:
                "100%",
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              auth.logout();
            }}
          >
            Logout
          </Button>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

Header.propTypes = {
  sx: PropTypes.object,
  customClass:
    PropTypes.string,
  toggleSidebar:
    PropTypes.func,
  toggleMobileSidebar:
    PropTypes.func,
};

export default Header;